export type VersionType = 'full' | 'standard' | 'basic';

export interface Subsection {
  id: string;
  title: string;
  content: string;
  listItems?: string[];
}

export interface Section {
  id: string;
  number: string;
  title: string;
  content: string;
  subsections?: Subsection[];
  listItems?: string[];
  versions: VersionType[];
  isAppendix?: boolean;
}

export const documentInfo = {
  company: "WLE Technology Ventures Sdn. Bhd. — 202601037405 (1699500-H) / WLE Ventures (Malaysia)",
  document: "Purchase Order Terms & Conditions",
  lastUpdated: "12 December 2025",
  version: "1.0"
};

export const versionDescriptions = {
  full: {
    title: "Full Version",
    description: "Custom fabricators, manufacturers",
    badge: "Complete"
  },
  standard: {
    title: "Standard Version", 
    description: "Equipment suppliers, components",
    badge: "Standard"
  },
  basic: {
    title: "Basic Version",
    description: "Raw material stockists, metals",
    badge: "Essential"
  }
};

export const sections: Section[] = [
  {
    id: "definitions",
    number: "1",
    title: "DEFINITIONS",
    content: "In these terms and conditions, unless the context otherwise requires, the following words and expressions shall have the meanings set out opposite them:",
    subsections: [
      {
        id: "def-purchaser",
        title: "The Purchaser",
        content: "WLE Technology Ventures Sdn. Bhd. — 202601037405 (1699500-H) and/or WLE Ventures (Malaysia), including their subsidiaries, affiliates, and related companies operating under the WLE Group (hereinafter referred to as \"WLE\")"
      },
      {
        id: "def-supplier",
        title: "The Supplier/Vendor",
        content: "The person, firm or company to whom the Purchase Order is addressed and who has agreed to supply the Goods to the Purchaser"
      },
      {
        id: "def-goods",
        title: "The Goods",
        content: "The goods agreed to be supplied and/or works to be performed by Supplier/Vendor to the Purchaser"
      },
      {
        id: "def-specification",
        title: "The Specification",
        content: "The specifications and/or description contained in the Purchase Order"
      },
      {
        id: "def-po",
        title: "The Purchase Order",
        content: "The written order placed with the Supplier/Vendor and issued by the Purchaser to the Supplier for the supply of the Goods"
      }
    ],
    versions: ['full', 'standard', 'basic']
  },
  {
    id: "application",
    number: "2",
    title: "APPLICATION OF THE TERMS AND CONDITIONS",
    content: "The terms and conditions herein shall be deemed to have been duly agreed and accepted by the Supplier/Vendor upon receipt of the Purchase Order unless communicated by the Supplier/Vendor to the Purchaser within five (5) calendar days from the date of receipt. The Schedules and Appendixes hereto shall form part of the terms and conditions and shall be read, taken and construed as essential parts of these terms and conditions.",
    versions: ['full', 'standard', 'basic']
  },
  {
    id: "goods",
    number: "3",
    title: "THE GOODS",
    content: "The specification of the Goods, its quality and quantity to be supplied by the Supplier/Vendor must strictly comply with the description stated in the Purchase Order.",
    versions: ['full', 'standard', 'basic']
  },
  {
    id: "price",
    number: "4",
    title: "PRICE",
    content: "The price of the Goods to be supplied by the Supplier/Vendor must strictly comply with the quoted amount stated in the Purchase Order. The price shown shall include the costs incurred for packing, insurance, duty and delivery destination, unless otherwise specified.",
    versions: ['full', 'standard', 'basic']
  },
  {
    id: "inspection",
    number: "5",
    title: "INSPECTION",
    content: "The Purchaser shall have the right of inspection on the Goods delivered by the Supplier/Vendor and retains the sole right and/or discretion to accept or reject the Goods subject to the agreed specification, quality and quantity stated in the Purchase Order. The Supplier/Vendor shall warrant that the Goods supplied shall be in the same condition as inspected and accepted by the Purchaser.",
    versions: ['full', 'standard']
  },
  {
    id: "quality-assurance",
    number: "6",
    title: "QUALITY ASSURANCE",
    content: "The Supplier/Vendor hereby undertakes to comply with the Purchaser's quality assurance requirements as provided in Appendix 1. The scope of requirements shall commence from the issuance of the Purchase Order until delivery of the Goods whereupon Clause 8 (Warranty) shall come into effect.",
    versions: ['full']
  },
  {
    id: "confidential",
    number: "7",
    title: "CONFIDENTIAL & NON-DISCLOSURE",
    content: "The Supplier/Vendor agrees not to disclose any information concerning the Purchase Order and these terms and conditions to any party without the written consent of the Purchaser.",
    subsections: [
      {
        id: "conf-leased",
        title: "7.1 Leased Document Protection",
        content: "The Supplier/Vendor shall not copy, duplicate or disclose the Leased Document and these terms and conditions to any party without written consent by the Purchaser, even to its subcontractor or Manufacturing Entrustee, whether in whole or in part."
      },
      {
        id: "conf-care",
        title: "7.2 Document Care and Usage",
        content: "The Supplier/Vendor shall keep and maintain the Leased Document with care and shall not use them for any purpose other than the manufacturing of the goods or take any action inconsistent with the Purchaser's rights, including divulging content to any party, using the document to manufacture equivalent or similar products, or allowing any party access to the Leased Document."
      },
      {
        id: "conf-return",
        title: "7.3 Document Return",
        content: "The Supplier/Vendor shall promptly return the Leased Document in full to the Purchaser at the Supplier/Vendor's sole expense upon:",
        listItems: [
          "(a) The Purchaser's request",
          "(b) Completion of obligations under the Purchase Order",
          "(c) Termination of the Purchase Order"
        ]
      },
      {
        id: "conf-competitive",
        title: "7.4 Competitive Restriction",
        content: "The Supplier/Vendor is strictly prohibited from using the data to facilitate their business to the Supplier/Vendor's competitor and customers directly or indirectly without first obtaining the Purchaser's written consent."
      },
      {
        id: "conf-sales",
        title: "7.5 Sales Restriction",
        content: "The Supplier/Vendor is strictly prohibited from selling any parts of the Goods to the Supplier/Vendor's customer directly without Purchaser's written consent. If there is any similar inquiry directly from Purchaser's customer or competitors, the Supplier/Vendor shall forthwith notify the Purchaser."
      },
      {
        id: "conf-breach",
        title: "7.6 Breach Consequences",
        content: "In the event the Supplier/Vendor breaches any agreed terms, without prejudice to other rights and remedies, the Supplier/Vendor's service shall be terminated upon issuance of 24-hours written notice by the Purchaser and the Supplier/Vendor will be blacklisted from any of the Purchaser's business."
      }
    ],
    versions: ['full', 'standard']
  },
  {
    id: "warranties",
    number: "8",
    title: "WARRANTIES AND UNDERTAKINGS BY THE SUPPLIER/VENDOR",
    content: "",
    subsections: [
      {
        id: "warranty-period",
        title: "8.1 Warranty Period",
        content: "The Supplier/Vendor warrants that the Goods supplied shall be free from defects in design, materials and workmanship and shall at all times conform with the Purchaser's specifications including performance specifications. The Supplier/Vendor shall be liable for damages suffered by the Purchaser in the event of default in warranty. The warranty period shall commence within twelve (12) months from the date of commissioning of the Goods or eighteen (18) months from the delivery date, whichever is earlier."
      },
      {
        id: "warranty-remedies",
        title: "8.2 Warranty Remedies",
        content: "Without prejudice to any Purchaser's rights and remedies, the Supplier/Vendor shall:",
        listItems: [
          "(a) Return defective parts or whole of the Goods to the Supplier/Vendor's workshop for repair",
          "(b) Deliver to the designated place by the Purchaser at the Supplier/Vendor's expense",
          "(c) Replace the part or whole of the Goods as soon as practicable at its expense",
          "(d) Dispatch its engineer(s) to the plant at its expense to repair or replace the defective part"
        ]
      }
    ],
    versions: ['full']
  },
  {
    id: "changes",
    number: "9",
    title: "CHANGES",
    content: "The Purchaser shall have the right to make changes in writing within the timeframe specified in the Purchaser's Request for Quotation (RFQ) for:",
    listItems: [
      "(a) The specifications, drawing and sample, if any",
      "(b) The articles and materials (including quantity without consequent change to the price stated in the Purchase Order and the delivery schedule thereof shall be furnished by the Supplier)"
    ],
    versions: ['full', 'standard']
  },
  {
    id: "termination",
    number: "10",
    title: "TERMINATION",
    content: "",
    subsections: [
      {
        id: "term-right",
        title: "10.1 Right to Terminate",
        content: "In the event of any breach by the Supplier/Vendor, the Supplier/Vendor agrees that the Purchaser shall have the absolute right to terminate this order in whole by serving seven (7) days' written notice, at its sole discretion."
      },
      {
        id: "term-effective",
        title: "10.2 Effective Date",
        content: "Such termination shall become effective upon receipt of such notice and the Supplier/Vendor shall stop work immediately."
      },
      {
        id: "term-claims",
        title: "10.3 No Claims",
        content: "The Supplier/Vendor shall have no claims for damages and/or compensation against the Purchaser upon such termination."
      }
    ],
    versions: ['full', 'standard']
  },
  {
    id: "delivery",
    number: "11",
    title: "DELIVERY OF THE GOODS",
    content: "",
    subsections: [
      {
        id: "del-schedule",
        title: "11.1 Delivery Schedule",
        content: "Delivery of the Goods shall be strictly in accordance with the Schedule, the expected arrival date stated in the Purchase Order and Appendix 2."
      },
      {
        id: "del-early",
        title: "11.2 Early Delivery",
        content: "If the date of shipment is ahead of the Schedule, it must be first communicated and agreed by the Purchaser."
      },
      {
        id: "del-delayed",
        title: "11.3 Delayed Delivery",
        content: "If the Supplier/Vendor fails to meet the Schedule, the Supplier/Vendor shall pay liquidated damages as stipulated under Term 16."
      },
      {
        id: "del-incoterms",
        title: "11.4 Incoterms",
        content: "All delivery terms shall comply with Incoterms 2022."
      }
    ],
    versions: ['full', 'standard', 'basic']
  },
  {
    id: "invoicing",
    number: "12",
    title: "INVOICING",
    content: "",
    subsections: [
      {
        id: "inv-requirements",
        title: "12.1 Invoice Requirements",
        content: "All invoices, advice notes and correspondence shall quote the Purchase Order number and Job Number. These shall be sent to the corresponding address in writing by registered mail, electronic mail and/or personal delivery."
      },
      {
        id: "inv-separate",
        title: "12.2 Separate Invoicing",
        content: "Goods supplied for different orders shall be invoiced separately to the Purchaser."
      }
    ],
    versions: ['full', 'standard', 'basic']
  },
  {
    id: "delivery-notes",
    number: "13",
    title: "DELIVERY NOTES",
    content: "",
    subsections: [
      {
        id: "dn-info",
        title: "13.1 Required Information",
        content: "The Goods delivered shall be accompanied by Delivery Notes showing the Order Number, quantity and the Supplier/Vendor's name clearly."
      },
      {
        id: "dn-compliance",
        title: "13.2 Non-Compliance",
        content: "Non-compliance with this Term shall render the Goods not accepted. Such non-accepted goods shall be kept and held by the Supplier/Vendor at its own risk and if not cleared within specified time, shall be returned to the Supplier/Vendor at the Supplier/Vendor's costs and expenses."
      }
    ],
    versions: ['full', 'standard', 'basic']
  },
  {
    id: "advertising",
    number: "14",
    title: "ADVERTISING",
    content: "The Supplier/Vendor shall not, without the written consent of the Purchaser, advertise or announce or allow to be announced that the Goods have been rendered to this Purchase Order.",
    versions: ['full', 'standard']
  },
  {
    id: "liability",
    number: "15",
    title: "LIABILITY",
    content: "The Purchaser shall not be liable and the Supplier/Vendor shall indemnify and keep the Purchaser indemnified for any loss or damage by fire or explosion or any loss or damage to life or property and injury caused by any person taking part therein, during the period of such work of contract/service repair, or of such nature pertaining to the Purchase Order.",
    versions: ['full', 'standard']
  },
  {
    id: "documentation",
    number: "16",
    title: "DOCUMENTATION",
    content: "The Supplier/Vendor shall supply electronic copy including but not limited to:",
    subsections: [
      {
        id: "doc-technical",
        title: "16.1 Technical Documents",
        content: "(a) The revised and final version of any drawings, plans, quality document, manual, testing records and other relevant documents stated in the Purchase Order"
      },
      {
        id: "doc-shipping",
        title: "16.2 Shipping Documents",
        content: "(b) Two (2) sets of Original Shipping Documents:",
        listItems: [
          "(i) B/L, invoice and packing list must reach Purchaser one (1) week before shipment's arrival, and port storage charges incurred due to vendor's negligence shall be borne by the Supplier/Vendor"
        ]
      }
    ],
    versions: ['full', 'standard']
  },
  {
    id: "late-delivery",
    number: "17",
    title: "LATE DELIVERY LIQUIDATED DAMAGES",
    content: "In the event of any delay in delivery on the arrival date as written in the Purchase Order, the Purchaser is entitled to claim late delivery liquidated damages levied as follows:",
    listItems: [
      "(i) 0.1% of the amount stated in the Purchase Order per day for the first 10 delayed days",
      "(ii) 0.5% of the amount stated in the Purchase Order per day for following delayed days"
    ],
    subsections: [
      {
        id: "ld-max",
        title: "Maximum Penalty",
        content: "Capped at 15% of the amount stated in the Purchase Order."
      },
      {
        id: "ld-drawing",
        title: "Drawing Delays",
        content: "For any delay in submission of drawing as per schedule, a delay penalty of RM50.00 per day shall be levied."
      },
      {
        id: "ld-nonconf",
        title: "Non-conformance",
        content: "If non-conformance is found, payment shall be held until rectification and prevention action has been made, or otherwise specified."
      },
      {
        id: "ld-breach",
        title: "Breach Consequences",
        content: "If the Supplier breaches agreed terms, the Supplier's service shall be terminated and blacklisted from any Purchaser business."
      }
    ],
    versions: ['full', 'standard', 'basic']
  },
  {
    id: "governing-law",
    number: "18",
    title: "GOVERNING LAW & JURISDICTION",
    content: "This terms and conditions shall be governed by and construed in accordance with the laws of Malaysia. In enforcing these terms, the Purchaser shall be at liberty to initiate action or proceedings against the Supplier/Vendor in Malaysia and/or elsewhere as the Purchaser may deem fit. The parties agree that where any actions or proceedings are initiated in Malaysia, they shall submit to the exclusive jurisdiction of the Courts of Malaysia in all matters connected with obligations and liabilities arising out of these terms and conditions. Service of any writ or summons or legal process may be effected on the Supplier/Vendor or Purchaser by forwarding a copy by prepaid registered post to their respective addresses.",
    versions: ['full', 'standard', 'basic']
  },
  {
    id: "successor",
    number: "19",
    title: "SUCCESSOR BOUND",
    content: "This Agreement shall be binding upon the heirs, personal or legal representatives and successors-in-title of the Supplier/Vendor and on the successors-in-title and assigns of the Purchaser.",
    versions: ['full', 'standard']
  },
  {
    id: "time-essence",
    number: "20",
    title: "TIME IS OF THE ESSENCE",
    content: "Time shall be of the essence in relation to these terms and conditions.",
    versions: ['full', 'standard', 'basic']
  },
  {
    id: "appendix-1",
    number: "Appendix 1",
    title: "QUALITY ASSURANCE",
    content: "",
    isAppendix: true,
    subsections: [
      {
        id: "qa-fabrication",
        title: "1. Fabrication of Goods",
        content: "(a) Fabrication of the Goods shall be in accordance with the Purchase Specifications, e.g., drawings or instructions."
      },
      {
        id: "qa-identification",
        title: "2. Goods Identification Method",
        content: "(a) The Goods must be printed or tagged with job number, name of goods, documents number, parts number, material number, according to manufacturer standard identification method, unless otherwise specified by the Purchaser.\n\nDuring shipment, the Goods must be packed according to its type and every package or box (or casing) must be marked with job number, name of goods, item number, parts number, material number, unless specified otherwise by the Purchaser."
      },
      {
        id: "qa-preservation",
        title: "3. Preservation of Goods",
        content: "(a) After conforming the ordered goods, good preservation must be ensured using all appropriate equipment and the Goods must be manufactured in proper manner in appropriate environments.\n\n(b) The manufacturing procedure must be made clear and concise for manufacturing personnel to complete work satisfactorily."
      },
      {
        id: "qa-measuring",
        title: "4. Proof-reading of Measuring Instruments",
        content: "The measuring instruments must be proof-read before usage in testing and checking. Preservation method must be made for both hardware and software to avoid annulment of proof-reading setting. If any testing is conducted using instruments with bad proof-reading, the testing result must be studied and the Purchaser must be informed promptly. Before further instruction is given by the Purchaser, the Supplier/Vendor must keep the Goods in safe and good condition. The disposal/treatment of the Goods must follow the instruction given by the Purchaser."
      },
      {
        id: "qa-qualifying",
        title: "5. Qualifying of the Goods",
        content: "All tested goods must be labelled \"Pass\" or \"Fail\". Rejected goods labelled \"Fail\" must be separated and treated according to manufacturer standard method of treating rejected goods. Tagging and labelling must not affect the quality of goods."
      },
      {
        id: "qa-nonconf",
        title: "6. Non-conformances",
        content: "If non-conformance of the Goods is found, it must be reported to WLE purchasing personnel. The Goods must be isolated."
      },
      {
        id: "qa-others",
        title: "7. Others",
        content: "The preservation, storage, packing and shipment of goods must follow the manufacturer's instruction if no other instruction has been specified earlier. The Purchaser has the right to conduct checking on quality assurance whenever necessary."
      }
    ],
    versions: ['full']
  },
  {
    id: "appendix-2",
    number: "Appendix 2",
    title: "DELIVERY CONTROL",
    content: "",
    isAppendix: true,
    subsections: [
      {
        id: "dc-docs",
        title: "1. Delivery Documents Requirements",
        content: "The following items must be included in delivery documents:",
        listItems: [
          "(a) The Purchaser's Job Number",
          "(b) The Purchaser's Drawing Number and Purchase Order Number",
          "(c) The Item number",
          "(d) The Item name",
          "(e) Quantity",
          "(f) The Package quantity (if more than 2 packages, number of packages must be stated)"
        ]
      },
      {
        id: "dc-packing",
        title: "2. Packing Requirements",
        content: "",
        listItems: [
          "(a) Job number, drawing number, Purchase Order number, name of goods, goods specifications, quantity, etc. must be written on the box or on piece of paper, followed by inserting it in a plastic folder and pasting it onto the box.",
          "(b) Different job number Goods must be packed separately.",
          "(c) Packing of the Goods and delivery must be done according to Purchase Order number or drawing number.",
          "(d) PVC piping materials, Diaphragm Valves, Ball Valves, Butterfly Valves, etc. must be packed and delivered according to specifications. The specifications must be displayed clearly.",
          "(e) Small items (Pumps parts, spare parts, joints, etc.) must be packed according to specifications, placed inside a plastic bag and the specifications must be displayed clearly."
        ]
      },
      {
        id: "dc-partial",
        title: "3. Partial Delivery",
        content: "Items with same drawing or Purchase Order number must be delivered in one delivery. If partial delivery is required, the Purchaser's personnel must be informed first prior to delivery."
      },
      {
        id: "dc-after",
        title: "4. After Delivery",
        content: "If non-conformance occurs, notification must be made promptly to the Purchaser's personnel.\n\nNote: For any above stated conditions which is not achievable, kindly submit deviation application for approval."
      }
    ],
    versions: ['full', 'standard']
  }
];

export const getFilteredSections = (version: VersionType): Section[] => {
  return sections.filter(section => section.versions.includes(version));
};
